import { useParameterStore } from '@/src/services/Statsig/PlatformStatsig'
import { MortgageModuleWebviewFlow } from '@/src/experiences/mortgage/statsig/store/useMortgageModuleWebviewFlowParameterStore'
import type { CardPolicyAndProcedures } from '@/src/experiences/card-application/statsig/parameter-stores'
import { CardDisputeURL } from '@/src/services/Statsig/useCardDisputeURLParameterStore'
import { VersionWallStore } from '@/src/experiences/updates/statsig/store/useVersionWallStore'
import { CardDisclosure } from '@/src/services/Statsig/useCardDisclosureParameterStore'
import { ReferralShareSheetMessage } from './useReferralShareSheetMessageParameterStore'
import { CardBenefitsStore } from '@/src/experiences/card-benefits/statsig/store/useCardBenefitsParameterStore'
import { Referral } from './useReferralParameterStore'
import { CardDesign } from './useCardDesignParameterStore'
import { MortgageModule } from '@/src/experiences/mortgage/statsig/store/useMortgageModuleDesignParameterStore'
import type { AppStoreParams } from '@/src/experiences/surveys/statsig/store/useAppStoreParamsParameterStore'
import { CardPaymentsStore } from '@/src/experiences/card-payments/statsig/store/useCardPaymentsParameterStore'

type ParameterStore = Record<string, unknown>

type ParameterStores = Record<string, ParameterStore>

type DefineParameterStores<T extends ParameterStores> = T

/** @see {@link https://console.statsig.com/1G3Xqca75leoEUUlzU7xDl/param_stores} */
type MesaParameterStore = DefineParameterStores<{
  'mortgage_module_-_webview_flow': MortgageModuleWebviewFlow
  card_policy_and_procedures: CardPolicyAndProcedures
  transaction_dispute_url: CardDisputeURL
  card_benefits: CardBenefitsStore
  version_wall: VersionWallStore
  card_landing_page_disclosure: CardDisclosure
  referral_share_sheet_message: ReferralShareSheetMessage
  referral: Referral
  mesa_card_design: CardDesign
  mesa_mortgage_module: MortgageModule
  app_store_params: AppStoreParams
  card_payments: CardPaymentsStore
}>

/** @see {@link MesaParameterStore} */
export function useMesaParameterStore<ParameterStoreName extends keyof MesaParameterStore>(
  parameterStoreName: ParameterStoreName,
) {
  const parameterStore = useParameterStore(parameterStoreName)
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return parameterStore as Omit<typeof parameterStore, 'get'> & {
    /**
     * @see {@link https://github.com/statsig-io/js-client-monorepo/blob/bc92c5fa2edb648443c5a6486deff7e82731c012/packages/client-core/src/StatsigTypeFactories.ts#L97}
     * This override enforces the fallback type to be the same as the ParameterName type to avoid
     * a potential footgun. Statsig, by default, will return the fallback if the parameter store
     * and fallback types don't match even if the parameter store was fetched successfully.
     */
    get: <
      ParameterName extends keyof MesaParameterStore[ParameterStoreName],
      const Fallback extends MesaParameterStore[ParameterStoreName][ParameterName] | undefined,
    >(
      parameterName: ParameterName,
      fallback?: Fallback,
    ) => MesaParameterStore[ParameterStoreName][ParameterName] | Fallback
  }
}
